import React, { ReactElement } from 'react';
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { LineChartData } from "./chartTypes";
import de from "apexcharts/dist/locales/de.json";
import { nanosecondsTimeStampToLocalString, nanosecondsTimeStampToUTCString } from "../../utils/utils";
import "./linechart.css";
import { ereignistyp } from '../../routes/netzwirtschaft/netzwirtschaftTypes';

const getFormatterForTooltip = (isNanoseconds: boolean, isUTC: boolean) => {
  return isNanoseconds
    ? { formatter: (val: number) => isUTC ? nanosecondsTimeStampToUTCString(val) : nanosecondsTimeStampToLocalString(val) }
    : { format: "dd.MM.yy HH:mm:ss" };
};

const getFormatterForXaxis = (isNanoseconds: boolean, isUTC: boolean) => {
  return isNanoseconds
    ? {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
      formatter: (val: string, timestamp: number, opts: any) =>
        isUTC ? nanosecondsTimeStampToUTCString(timestamp).split(",") : nanosecondsTimeStampToLocalString(timestamp).split(","),
    }
    :
    { datetimeUTC: isUTC ? true : false };
};

let initMinX = 0;
let initMaxX = 0;
let minX = 0;
let maxX = 0;

function zoomIn(chartID: string) {
  const centerX = (minX + maxX) / 2;
  const newMinX = (minX + centerX) / 2;
  const newMaxX = (maxX + centerX) / 2;
  ApexCharts.exec(chartID, "updateOptions", {
    xaxis: {
      min: newMinX,
      max: newMaxX,
    }
  });
  minX = newMinX;
  maxX = newMaxX;
}

function zoomOut(chartID: string) {
  const centerX = (minX + maxX) / 2;
  const newMinX = minX - (centerX - minX);
  const newMaxX = maxX - (centerX - maxX);
  ApexCharts.exec(chartID, "updateOptions", {
    xaxis: {
      min: newMinX,
      max: newMaxX,
    }
  });
  minX = newMinX;
  maxX = newMaxX;
}

function resetZoom(chartID: string) {
  ApexCharts.exec(chartID, "updateOptions", {
    xaxis: {
      min: initMinX,
      max: initMaxX,
    }
  });
  minX = initMinX;
  maxX = initMaxX;
}

function generateGradients(data: LineChartData): any[] {
  const result = data.series.map(series => {
    return [{
      offset: 0,
      color: "lightgrey",
      opacity: 1,
    },
    {
      offset: (series.lastExtremum ?? 0.1) - 0.1,
      color: "lightgrey",
      opacity: 1,
    },
    {
      offset: series.lastExtremum ?? 0,
      color: series.color,
      opacity: 1,
    },
    {
      offset: 100,
      color: series.color,
      opacity: 1,
    }
    ]
  });
  return result;
}


export default function LineChart(data: LineChartData, isUTC: boolean, chartID: string): ReactElement {
  const lineChartOptions: ApexOptions = {
    chart: {
      id: chartID,
      zoom: {
        type: "x",
        enabled: true,
      },
      animations: {
        enabled: false,
      },
      locales: [de],
      defaultLocale: "de",
      events: {
        mounted: function (chartContext) {
          minX = chartContext.w.globals.minX;
          maxX = chartContext.w.globals.maxX;
          initMinX = minX;
          initMaxX = maxX;
        },
        zoomed: function (chartContext) {
          minX = chartContext.w.globals.minX;
          maxX = chartContext.w.globals.maxX;
        },
        scrolled: function (chartContext) {
          minX = chartContext.w.globals.minX;
          maxX = chartContext.w.globals.maxX;
        },
      }
    },

    xaxis: {
      type: data.isNanoseconds ? "numeric" : "datetime",
      labels: getFormatterForXaxis(data.isNanoseconds, isUTC),
      tickAmount: 10,
    },

    yaxis: {
      labels: {
        formatter: (val: number) => val?.toLocaleString("de-DE", { maximumFractionDigits: 2 }),
      },
      title: {
        text: data.yAxisTitleText,
        style: {
          fontWeight: 700,
        },
      },
      tickAmount: 10,
      forceNiceScale: true,
      min: data.series[0].lastExtremum != undefined ? 0 : undefined,
      max: data.series[0].lastExtremum != undefined ? 1 : undefined,
    },

    tooltip: {
      x: getFormatterForTooltip(data.isNanoseconds, isUTC),
    },

    stroke: {
      width: 1,
    },

    legend: {
      showForSingleSeries: true,
    },

    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.9,
        colorStops: generateGradients(data),
      }
    },

    markers: {
      discrete: data.singlePoints.flatMap(x => data.series.map((y, index) => ({
        seriesIndex: index,
        dataPointIndex: x,
        size: 1,
      })),
      )
    }
  };

  const getColorForType = (type: ereignistyp): string => {
    if (type == ereignistyp.netzereignis) return "#b2c1f7";
    if (type == ereignistyp.pqereignis) return "#f7deb2";
    if (type == ereignistyp.stoerschrieb) return "#b5faaf";
    return "#fff";
  }

  const ereignisseAnnotations: ApexOptions = {
    annotations: {
      xaxis: data.ereignisse.map(a => ({
        x: data.isNanoseconds ? a.zeit : a.zeit / 1000000,
        x2: data.isNanoseconds ? a.ende : a.ende / 1000000,
        fillColor: getColorForType(a.typ),
        label: {
          text: a.beschreibung,
        }
      })).concat(data.geflaggteMesswerte.map(a => ({
        x: data.isNanoseconds ? a.start : a.start / 1000000,
        x2: data.isNanoseconds ? a.ende : a.ende / 1000000,
        fillColor: "#b1b1b1",
        label: {
          text: "Verworfene Messwerte laut Regel: " + a.regel,
        }
      }))),
    }
  }

  const geflaggteMesswerteAnnotations: ApexOptions = {
    annotations: {
      xaxis: data.geflaggteMesswerte.map(a => ({
        x: data.isNanoseconds ? a.start : a.start / 1000000,
        x2: data.isNanoseconds ? a.ende : a.ende / 1000000,
        fillColor: "#b1b1b1",
        label: {
          text: "Verworfene Messwerte laut Regel: " + a.regel,
        }
      })),
    }
  }

  return (
    <div id="chartDiv" onContextMenu={(event) => {
      event.preventDefault();
      resetZoom(chartID);
    }} onWheel={(event) => {
      event.preventDefault();
      event.nativeEvent.preventDefault();
      event.deltaY > 0 ? zoomOut(chartID) : zoomIn(chartID);
    }}>
      <ReactApexChart
        options={Object.assign({}, lineChartOptions, data.showEreignisse ? ereignisseAnnotations : geflaggteMesswerteAnnotations)}
        series={data.series}
        type="line"
        height={700}
      />
    </div>
  );
}
